import Card from "components/DesignSystem/Card/Card";
import Slider from "components/DesignSystem/Slider/Slider";
import { ArrowLeft } from "components/icons/ArrowLeft";
import { CategorizeIcon } from "components/icons/CategorizeIcon";
import { CreateBillIcon } from "components/icons/CreateBillIcon";
import { CreateInvoiceIcon } from "components/icons/CreateInvoiceIcon";
import { ThreadChat } from "components/Thread/ThreadChat";
import dayjs from "dayjs";
import { memo, useEffect, useMemo, useState } from "react";
import { MessageResponse } from "stream-chat";
import type { DefaultStreamChatGenerics } from "stream-chat-react";
import { Channel, useChatContext } from "stream-chat-react";
import { usePaginatedMessages } from "hooks/usePaginatedMessages";
import { useUpdateQuery } from "hooks/useQuery";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  EntityBanner,
  HistoryChatHeader,
  InkleAIChatSliderHeader,
  InkleAIChatSliderLeftBar,
} from "./InkleAIChatHeaders";
import { InkleAIEmptyState } from "./InkleAIChatEmptyState";
import { ProductTour } from "components/DesignSystem/ProductTour/ProductTour";
import { Step } from "react-joyride";
import {
  useGetOnBoardingQuery,
  useUpdateOnBoardingMutation,
} from "store/apis/inkleAI";
import { useAnalytics } from "hooks/useAnalytics";
import {
  INKLE_AI_TOUR_STARTED,
  INKLE_AI_TOUR_COMPLETED,
  INKLE_AI_TOUR_SKIPPED,
} from "constants/analyticsEvents";
import { AI_TOUR_STEP_KEY, ONBOARDING_STATUS } from "constants/inkleAI";
import { Entity } from "types/Models/entity";
import { useRoleBasedView } from "hooks/useRoleBasedView";

type ViewState = "none" | "list" | "history";

export type InkleAIChatSliderProps = {
  open: boolean;
  onClose: () => void;
  aiChannelId?: string | null;
  entityName?: string;
  entities?: Entity[];
  currentEntityId?: string;
  shouldUpdateQuery?: boolean;
  initialThreadMessage?: MessageResponse<DefaultStreamChatGenerics> | null;
  initialPrefilledMessage?: string | null;
};

const EmptyStateWrapper = ({
  onExploreAllClick,
  prefilledMessage,
  onThreadMessageChange,
}: {
  onExploreAllClick?: () => void;
  prefilledMessage?: string;
  onThreadMessageChange: (
    message: MessageResponse<DefaultStreamChatGenerics>
  ) => void;
}) => (
  <InkleAIEmptyState
    onExploreAllClick={onExploreAllClick}
    prefilledMessage={prefilledMessage}
    onThreadMessageChange={onThreadMessageChange}
  />
);

const InkleAIChatThread = ({
  open,
  aiChannelId,
  threadMessage,
  onThreadMessageChange,
  prefilledMessage,
  onExploreAllClick,
}: {
  open: boolean;
  aiChannelId?: string;
  threadMessage: MessageResponse<DefaultStreamChatGenerics> | null;
  onThreadMessageChange: (
    message: MessageResponse<DefaultStreamChatGenerics>
  ) => void;
  prefilledMessage?: string;
  onExploreAllClick?: () => void;
}) => {
  const { client } = useChatContext();
  const [aiChannel, setAiChannel] = useState<any>(null);
  const [isLoadingChannel, setIsLoadingChannel] = useState(false);

  useEffect(() => {
    const initChannel = async () => {
      setIsLoadingChannel(true);
      try {
        const channel = client.channel("messaging", aiChannelId);
        await channel.watch();
        setAiChannel(channel);
      } catch (error) {
        console.error({
          message: "Unable to connect to AI channel. Please try again.",
        });
      } finally {
        setIsLoadingChannel(false);
      }
    };

    initChannel();
  }, [aiChannelId, client]);

  useEffect(() => {
    if (!aiChannel || !threadMessage) return;

    const scrollToBottom = () => {
      requestAnimationFrame(() => {
        const scrollContainer = document.querySelector(".threadBody");
        if (!scrollContainer) return;
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      });
    };

    const handleMessageUpdate = (event: any) => {
      if (event.message?.id === threadMessage.id) {
        onThreadMessageChange(event.message);
      }

      if (event.message?.parent_id === threadMessage.id) {
        scrollToBottom();
      }
    };

    const handleNewMessage = (event: any) => {
      if (event.message?.parent_id === threadMessage.id) {
        scrollToBottom();
      }
    };

    aiChannel.on("message.updated", handleMessageUpdate);
    aiChannel.on("message.new", handleNewMessage);

    return () => {
      aiChannel.off("message.updated", handleMessageUpdate);
      aiChannel.off("message.new", handleNewMessage);
    };
  }, [aiChannel, threadMessage, onThreadMessageChange]);

  const emptyStateComponent = useMemo(
    () => () =>
      (
        <EmptyStateWrapper
          onExploreAllClick={onExploreAllClick}
          prefilledMessage={prefilledMessage}
          onThreadMessageChange={onThreadMessageChange}
        />
      ),
    [onExploreAllClick, prefilledMessage, onThreadMessageChange]
  );

  if (!aiChannelId || isLoadingChannel || !aiChannel) {
    return (
      <div className="t-h-full t-flex t-items-center t-justify-center">
        <div className="t-text-text-30">
          {!aiChannelId && "AI channel not available"}
        </div>
      </div>
    );
  }

  return (
    <div className="t-w-full t-h-full">
      <Channel channel={aiChannel}>
        <ThreadChat
          open={open}
          onClose={() => {}}
          threadMessage={threadMessage}
          headerDescription=""
          isSummaryThread={false}
          EmptyStateComponent={emptyStateComponent}
          hideHeader={true}
        />
      </Channel>
    </div>
  );
};

const InkleAIListView = ({
  onBack,
  onActionClick,
}: {
  onBack: () => void;
  onActionClick: (message: string) => void;
}) => {
  const actions = [
    // {
    //   heading: "Categorize transactions",
    //   subheading: "Automatically classify uncategorized entries.",
    //   icon: <CategorizeIcon />,
    // },
    {
      heading: "Create an invoice",
      subheading: "Generate and share invoices in a few clicks.",
      icon: <CreateInvoiceIcon />,
    },
    {
      heading: "Create a bill",
      subheading:
        "Upload or enter bill details and let Inkle AI record it for you.",
      icon: <CreateBillIcon />,
    },
    {
      heading: "Create bill from uploaded document",
      subheading:
        "Upload a bill document and let Inkle AI parse and add it to the system.",
      icon: <CreateBillIcon />,
    },
    {
      heading: "Reconciliation",
      subheading:
        "Let Inkle AI match, detect gaps, and tidy up your books instantly.",
      icon: <CreateBillIcon />,
    },
  ];

  return (
    <div className="t-bg-neutral-0 t-rounded-tl-2xl t-h-full t-flex t-flex-col t-p-4">
      <button
        onClick={onBack}
        className="all:unset t-flex t-items-center t-cursor-pointer"
      >
        <ArrowLeft size="15" />
        <div className="t-font-medium t-text-text-60 t-ml-3">
          Inkle AI Action Library
        </div>
      </button>

      <div className="t-flex t-flex-col t-gap-3 t-mt-6">
        {actions.map((action, idx) => (
          <div key={idx} className="t-relative t-overflow-hidden t-rounded-xl">
            <div
              className="t-absolute t-left-0 t-top-1 t-bottom-1 t-w-0.5 t-z-10 t-rounded-full"
              style={{
                background:
                  "linear-gradient(180deg, #FB6FD5, #C65DE7, #8A4ED4)",
              }}
            />

            <Card.Root
              onClick={() => {
                onActionClick(action.heading);
              }}
              borderRounded="xl"
              variant="secondary"
              className="t-bg-white hover:t-shadow-sm t-transition-shadow t-cursor-pointer t-relative"
            >
              <Card.Body className="t-flex t-items-center t-gap-4">
                <div className="t-w-12 t-h-12 t-bg-neutral-0 t-rounded-lg t-flex t-items-center t-justify-center t-flex-shrink-0">
                  {action.icon}
                </div>

                <div className="t-flex-1">
                  <div className="t-text-body-sm t-font-medium t-text-text-100">
                    {action.heading}
                  </div>

                  <div className="t-text-subtext-sm t-text-text-30 t-mt-1">
                    {action.subheading}
                  </div>
                </div>

                <div className="t-text-neutral-40 t-rotate-180 t-flex-shrink-0">
                  <ArrowLeft size="16" />
                </div>
              </Card.Body>
            </Card.Root>
          </div>
        ))}
      </div>
    </div>
  );
};

const InkleAIHistoryView = ({
  aiChannelId,
  onBack,
  onThreadSelect,
  entities = [],
}: {
  aiChannelId: string;
  onBack: () => void;
  onThreadSelect: (message: MessageResponse<DefaultStreamChatGenerics>) => void;
  entities?: Entity[];
}) => {
  const { client } = useChatContext();
  const channel = useMemo(
    () => client.channel("messaging", aiChannelId),
    [client, aiChannelId]
  );
  const { isCustomer } = useRoleBasedView();

  const customerMemberIds = Object.entries(channel?.state?.members || {})
    .filter(([, value]) => !Boolean(value?.user?.service_team))
    .map(([, value]) => value?.user?.id);

  const messageFilters = isCustomer
    ? { reply_count: { $gte: 1 }, "user.id": { $in: customerMemberIds } }
    : { reply_count: { $gte: 1 } };

  const { messages, loading, loadNextPage, next } = usePaginatedMessages(
    client,
    { cid: `messaging:${aiChannelId}` },
    {},
    { limit: 25 },
    messageFilters
  );

  const getEntityName = (customData?: Record<string, any>) => {
    const entityId = customData?.entity_id as string | undefined;
    if (!entityId) return "";
    const entity = entities.find((e) => e.uuid === entityId);
    return entity?.name || "";
  };

  const threads = messages
    .map(({ message }) => message)
    .sort((a, b) => {
      const dateA = new Date(a.updated_at || 0).getTime();
      const dateB = new Date(b.updated_at || 0).getTime();
      return dateB - dateA;
    });

  const formatDate = (date?: string) =>
    date ? dayjs(date).format("MMM D, YYYY") : "";

  const renderEmptyState = () => (
    <div className="t-flex t-flex-col t-items-center t-justify-center t-flex-1">
      <div className="t-text-text-30 t-font-medium t-items-center">
        No messages yet
      </div>
    </div>
  );

  const scrollableTargetId = "history-scroll-container";

  const renderThreads = () => (
    <InfiniteScroll
      dataLength={threads.length}
      next={loadNextPage}
      hasMore={!!next}
      scrollThreshold={0.8}
      scrollableTarget={scrollableTargetId}
      loader={
        <div className="t-flex t-items-center t-justify-center t-py-4">
          <div className="t-text-text-30 t-text-sm">Loading more...</div>
        </div>
      }
    >
      <div className="t-flex t-flex-col t-gap-3 t-mt-4">
        {threads.map((thread) => (
          <div
            key={thread.id}
            className="t-relative t-overflow-hidden t-rounded-xl"
          >
            <Card.Root
              onClick={() => onThreadSelect(thread)}
              borderRounded="lg"
              variant="secondary"
              className="t-border-none hover:t-bg-neutral-10 t-cursor-pointer"
            >
              <Card.Body className="t-flex t-items-center t-gap-4">
                <div className="t-flex-1">
                  <div className="t-text-body-sm t-font-medium t-text-text-100 t-line-clamp-2">
                    {thread.text || "No message text"}
                  </div>

                  <div className="t-flex t-items-center t-gap-2 t-mt-1">
                    <div className="t-text-subtext-sm t-text-text-30">
                      Last updated: {formatDate(thread.updated_at)}
                    </div>
                    <div className="t-text-subtext-sm t-text-text-30">|</div>
                    <div className="t-text-subtext-sm t-text-text-30">
                      Created by: {thread.user?.name || "Unknown"}
                    </div>
                    {getEntityName(
                      thread.custom_data as Record<string, any>
                    ) && (
                      <>
                        <div className="t-text-subtext-sm t-text-text-30">
                          |
                        </div>
                        <div className="t-text-subtext-sm t-text-text-30">
                          Entity:{" "}
                          {getEntityName(
                            thread.custom_data as Record<string, any>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="t-text-neutral-40 t-rotate-180 t-flex-shrink-0">
                  <ArrowLeft size="16" />
                </div>
              </Card.Body>
            </Card.Root>
          </div>
        ))}
      </div>
    </InfiniteScroll>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <div className="t-flex t-items-center t-justify-center t-flex-1">
          <div className="t-text-text-30">Loading threads...</div>
        </div>
      );
    }

    if (threads.length === 0) {
      return renderEmptyState();
    }

    return renderThreads();
  };

  return (
    <div className="t-bg-neutral-0 t-rounded-tl-2xl t-h-full t-flex t-flex-col t-p-4">
      {/* Header */}
      <button
        onClick={onBack}
        className="all:unset t-flex t-items-center t-cursor-pointer t-mb-4"
      >
        <ArrowLeft size="15" />
        <div className="t-font-medium t-text-text-60 t-ml-3">
          Inkle AI Chat History
        </div>
      </button>

      <div
        id={scrollableTargetId}
        className="t-flex-1 t-overflow-y-auto t-pr-2"
      >
        {renderContent()}
      </div>
    </div>
  );
};

const InkleAIChatSliderContent = ({
  viewMode,
  onBackToChat,
  open,
  threadMessage,
  onThreadMessageChange,
  prefilledMessage,
  onActionClick,
  conversationKey,
  onListClick,
  onThreadSelect,
  isHistoryThread,
  setViewMode,
  aiChannelId,
  entityName = "",
  entities = [],
  currentEntityId,
  shouldUpdateQuery = true,
}: {
  viewMode: ViewState;
  onBackToChat: () => void;
  open: boolean;
  threadMessage: MessageResponse<DefaultStreamChatGenerics> | null;
  onThreadMessageChange: (
    message: MessageResponse<DefaultStreamChatGenerics>
  ) => void;
  prefilledMessage?: string;
  onActionClick: (message: string) => void;
  conversationKey: number;
  onListClick: () => void;
  onThreadSelect: (message: MessageResponse<DefaultStreamChatGenerics>) => void;
  isHistoryThread: boolean;
  setViewMode: React.Dispatch<React.SetStateAction<ViewState>>;
  aiChannelId?: string | null;
  entityName?: string;
  entities?: Entity[];
  currentEntityId?: string;
  shouldUpdateQuery?: boolean;
}) => {
  const { update: updateQuery } = useUpdateQuery();

  const getEntityNameFromId = (entityId?: string) => {
    if (!entityId) return "";
    const foundEntity = entities.find((e) => e.uuid === entityId);
    return foundEntity?.name || "";
  };

  const threadEntityId =
    (threadMessage?.custom_data as Record<string, any>)?.entity_id ||
    (threadMessage as any)?.message?.custom_data?.entity_id;

  const threadEntityName = threadEntityId
    ? getEntityNameFromId(threadEntityId)
    : "";

  const displayEntityName = threadEntityName || entityName;

  const shouldShowBanner = isHistoryThread ? !!displayEntityName : !!entityName;
  const bannerEntityName = isHistoryThread ? displayEntityName : entityName;

  useEffect(() => {
    if (
      shouldUpdateQuery &&
      threadEntityId &&
      threadEntityId !== currentEntityId &&
      !isHistoryThread
    ) {
      updateQuery({ query: "entity", value: threadEntityId });
    }
  }, [
    threadEntityId,
    currentEntityId,
    isHistoryThread,
    updateQuery,
    shouldUpdateQuery,
  ]);

  if (viewMode === "list") {
    return (
      <InkleAIListView onBack={onBackToChat} onActionClick={onActionClick} />
    );
  }

  if (viewMode === "history") {
    if (!aiChannelId) {
      return (
        <div className="t-h-full t-flex t-items-center t-justify-center">
          <div className="t-text-text-30">AI channel not available</div>
        </div>
      );
    }

    return (
      <InkleAIHistoryView
        aiChannelId={aiChannelId}
        onBack={onBackToChat}
        onThreadSelect={onThreadSelect}
        entities={entities}
      />
    );
  }

  return (
    <div className="t-bg-neutral-0 t-rounded-tl-2xl t-h-full t-flex t-flex-col t-border t-border-solid t-border-neutral-0">
      {shouldShowBanner && bannerEntityName && (
        <EntityBanner entityName={bannerEntityName} />
      )}

      {isHistoryThread && threadMessage && (
        <HistoryChatHeader
          conversationTitle={threadMessage.text || "Conversation"}
          onBackToHistory={() => setViewMode("history")}
        />
      )}

      <div className="t-flex-1 t-overflow-y-auto">
        <InkleAIChatThread
          key={conversationKey}
          open={open}
          aiChannelId={aiChannelId || undefined}
          threadMessage={threadMessage}
          onThreadMessageChange={onThreadMessageChange}
          prefilledMessage={prefilledMessage}
          onExploreAllClick={onListClick}
        />
      </div>
    </div>
  );
};

const InkleAIChatSliderComponent = ({
  open,
  onClose,
  aiChannelId,
  entityName = "",
  entities = [],
  currentEntityId,
  shouldUpdateQuery = true,
  initialThreadMessage,
  initialPrefilledMessage,
}: InkleAIChatSliderProps) => {
  const [isHistoryThread, setIsHistoryThread] = useState(
    () => !!initialThreadMessage
  );
  const [viewMode, setViewMode] = useState<ViewState>("none");
  const [threadMessage, setThreadMessage] =
    useState<MessageResponse<DefaultStreamChatGenerics> | null>(
      () => initialThreadMessage || null
    );
  const [prefilledMessage, setPrefilledMessage] = useState<string | undefined>(
    initialPrefilledMessage || undefined
  );
  const [conversationKey, setConversationKey] = useState(0);

  const handleThreadMessageChange = (
    message: MessageResponse<DefaultStreamChatGenerics>
  ) => {
    setThreadMessage(message);
    setPrefilledMessage(undefined);
  };

  const { data: workflowData, isSuccess: isWorkflowSuccess } =
    useGetOnBoardingQuery({
      step_key: AI_TOUR_STEP_KEY,
    });
  const [updateOnBoarding] = useUpdateOnBoardingMutation();
  const [shouldRunTour, setShouldRunTour] = useState(false);
  const { trackEvent } = useAnalytics();

  const tourStatus = workflowData?.status_display;

  useEffect(() => {
    if (
      isWorkflowSuccess &&
      (tourStatus === ONBOARDING_STATUS.IN_PROGRESS ||
        tourStatus === ONBOARDING_STATUS.NOT_STARTED) &&
      open &&
      viewMode === "none"
    ) {
      const timer = setTimeout(() => {
        const targetElement = document.querySelector(
          "#inkle-ai-get-started"
        ) as HTMLElement;
        if (targetElement && targetElement.offsetParent !== null) {
          setShouldRunTour(true);
        }
      }, 800);

      return () => {
        clearTimeout(timer);
      };
    }

    if (
      !open ||
      viewMode !== "none" ||
      tourStatus === ONBOARDING_STATUS.COMPLETED
    ) {
      setShouldRunTour(false);
    }
  }, [isWorkflowSuccess, tourStatus, open, viewMode]);

  const tourSteps: Step[] = [
    {
      target: "#inkle-ai-get-started",
      title: "Step 1 of 4",
      content:
        "Ask something to get started with Inkle AI or prompt using the quick suggestions below.",
      disableBeacon: true,
      placement: "top",
      offset: 12,
    },
    {
      target: "#inkle-ai-create-chat",
      title: "Step 2 of 4",
      content: "Create a new thread to start a fresh conversation or task.",
      disableBeacon: true,
      placement: "right",
      offset: 12,
    },
    {
      target: "#inkle-ai-history",
      title: "Step 3 of 4",
      content: "View all your previous conversations for quick reference.",
      disableBeacon: true,
      placement: "right",
      offset: 12,
    },
    {
      target: "#inkle-ai-action-library",
      title: "Step 4 of 4",
      content:
        "Browse all available AI-powered actions and launch one with a single click.",
      disableBeacon: true,
      placement: "right",
      offset: 12,
    },
  ];

  const handleListClick = () => {
    setViewMode("list");
  };
  const handleHistoryClick = () => {
    setViewMode("history");
  };
  const startNewThread = (prefill?: string) => {
    setThreadMessage(null);
    setIsHistoryThread(false);
    setPrefilledMessage(prefill);
    setConversationKey((k) => k + 1);
    setViewMode("none");
  };
  const handleCreateClick = () => startNewThread();

  const handleActionClick = (msg: string) => {
    startNewThread(msg);
  };

  const handleThreadSelect = (
    message: MessageResponse<DefaultStreamChatGenerics>
  ) => {
    setThreadMessage(message);
    setIsHistoryThread(true);
    setPrefilledMessage(undefined);
    setConversationKey((k) => k + 1);
    setViewMode("none");
  };

  const handleClose = () => {
    setThreadMessage(null);
    setViewMode("none");
    setPrefilledMessage(undefined);
    setIsHistoryThread(false);
    onClose();
  };

  const handleTourStart = () => {
    trackEvent(INKLE_AI_TOUR_STARTED);
  };

  const handleTourComplete = async () => {
    trackEvent(INKLE_AI_TOUR_COMPLETED);
    if (!workflowData?.uuid) return;
    try {
      await updateOnBoarding({
        uuid: workflowData.uuid,
        step_key: AI_TOUR_STEP_KEY,
        status: ONBOARDING_STATUS.COMPLETED,
      }).unwrap();
    } catch (error) {
      console.error("Failed to update workflow status:", error);
    }
  };

  const handleTourSkip = async () => {
    trackEvent(INKLE_AI_TOUR_SKIPPED);
    if (!workflowData?.uuid) return;
    try {
      await updateOnBoarding({
        uuid: workflowData.uuid,
        step_key: AI_TOUR_STEP_KEY,
        status: ONBOARDING_STATUS.SKIPPED,
      }).unwrap();
    } catch (error) {
      console.error("Failed to update workflow status:", error);
    }
  };

  return (
    <Slider.Root open={open} onOpenChange={handleClose}>
      <Slider.Content open={open} useCustomOverlay width={800}>
        <div className="t-relative t-h-full t-flex t-flex-col">
          <div className="t-flex t-justify-between t-flex-shrink-0">
            <InkleAIChatSliderHeader entityName={entityName} />
            <div className="t-p-2">
              <Slider.Close />
            </div>
          </div>

          <div className="t-flex t-flex-1 t-overflow-hidden">
            <div className="t-w-14 t-flex-shrink-0">
              <InkleAIChatSliderLeftBar
                onListClick={handleListClick}
                onCreateClick={handleCreateClick}
                onHistoryClick={handleHistoryClick}
                activeView={viewMode}
                isHistoryThread={isHistoryThread}
              />
            </div>

            <div className="t-flex-1 t-overflow-hidden">
              <InkleAIChatSliderContent
                conversationKey={conversationKey}
                viewMode={viewMode}
                onBackToChat={handleCreateClick}
                open={open}
                threadMessage={threadMessage}
                onThreadMessageChange={handleThreadMessageChange}
                prefilledMessage={prefilledMessage}
                onActionClick={handleActionClick}
                onListClick={handleListClick}
                onThreadSelect={handleThreadSelect}
                isHistoryThread={isHistoryThread}
                setViewMode={setViewMode}
                aiChannelId={aiChannelId}
                entityName={entityName}
                entities={entities}
                currentEntityId={currentEntityId}
                shouldUpdateQuery={shouldUpdateQuery}
              />
            </div>
          </div>

          <ProductTour
            steps={tourSteps}
            run={shouldRunTour}
            onComplete={handleTourComplete}
            onSkip={handleTourSkip}
            onStart={handleTourStart}
          />
        </div>
      </Slider.Content>
    </Slider.Root>
  );
};

export const InkleAIChatSlider = memo(InkleAIChatSliderComponent);
